(function () {
  const storageKey = "roar_selected_phone_model";
  const pendingConfirmationKey = "roar_phone_model_confirmation";
  const storageDuration = 90 * 24 * 60 * 60 * 1000;
  const selector = "[data-global-phone-selector]";
  const triggerSelector = "[data-global-phone-trigger]";
  const labelSelector = "[data-global-phone-label]";
  const optionSelector = "[data-global-phone-option]";
  const searchSelector = "[data-global-phone-search]";
  const clearSelector = "[data-global-phone-clear]";
  const emptySelector = "[data-global-phone-empty]";
  const ctaSelector = "[data-global-phone-cta]";
  const cardModelSelector = "[data-global-phone-card-model]";
  const productPickerSelector = "variant-picker[data-require-phone-model]";
  const productModelOptionSelector = "input[data-phone-model-option]";
  let selectedModel = "";
  let toastTimeout;
  let productRequirementsBound = false;

  const normalize = (value) => (value || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

  // Match each search term independently while preserving the real variant inputs.
  const matchesModelSearch = (model, query) => {
    const plain = value => String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const compactModel = plain(model).replace(/[^a-z0-9]/g, '');
    const terms = plain(query).split(/[^a-z0-9]+/).filter(Boolean);
    return terms.every(term => compactModel.includes(term));
  };


  const getDrawer = () => document.querySelector(selector);

  const getConfig = () => {
    const drawer = getDrawer();

    return {
      drawer,
      emptyLabel: drawer?.dataset.emptyLabel || "Choose phone",
      viewCasesLabel: drawer?.dataset.viewCasesLabel || "View phone cases",
      viewCasesForLabel: drawer?.dataset.viewCasesForLabel || "View cases for __MODEL__",
      selectedLabel: drawer?.dataset.selectedLabel || "Selected phone model: __MODEL__. Click to change.",
      emptyTriggerLabel: drawer?.dataset.emptyTriggerLabel || "Choose phone model",
      selectedConfirmation: drawer?.dataset.selectedConfirmation || "__MODEL__ selected",
      clearedConfirmation: drawer?.dataset.clearedConfirmation || "Showing all phone models",
      modelParamName: drawer?.dataset.modelParamName || "filter.v.option.model",
      knownParams: (drawer?.dataset.knownModelParamNames || "filter.v.option.model,filter.v.option.modell")
        .split(",")
        .map((param) => param.trim())
        .filter(Boolean),
      modelCollectionHandles: new Set(
        (drawer?.dataset.modelCollectionHandles || "mobilskal,slim-case,tough-case,magsafe-case,magnetic-case")
          .split(",")
          .map((handle) => handle.trim())
          .filter(Boolean)
      )
    };
  };

  const getCollectionHandle = (url) => {
    const match = url.pathname.match(/\/collections\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  };

  const getModelFromUrl = (url, knownParams) => {
    for (const paramName of knownParams) {
      const value = url.searchParams.get(paramName);

      if (value) {
        return value;
      }
    }

    return "";
  };

  const getStoredModel = () => {
    try {
      const storedValue = window.localStorage.getItem(storageKey);
      if (!storedValue) return "";

      try {
        const preference = JSON.parse(storedValue);

        if (typeof preference === "string") {
          storeModel(preference);
          return preference;
        }

        if (!preference?.value || !preference?.expiresAt || preference.expiresAt <= Date.now()) {
          clearStoredModel();
          return "";
        }

        return preference.value;
      } catch (error) {
        // Migrate the previous plain-text value without losing the customer's choice.
        storeModel(storedValue);
        return storedValue;
      }
    } catch (error) {
      return "";
    }
  };

  const storeModel = (model) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({
        value: model,
        updatedAt: Date.now(),
        expiresAt: Date.now() + storageDuration
      }));
    } catch (error) {
      // Some browsers block storage in strict privacy contexts.
    }
  };

  const clearStoredModel = () => {
    try {
      window.localStorage.removeItem(storageKey);
    } catch (error) {
      // Some browsers block storage in strict privacy contexts.
    }
  };

  const compatibilityCache = new Map();
  let familyNavigationRequest = 0;

  const compatibilityKey = (url, model) => url.origin + url.pathname + "|" + normalize(model);

  // Ask Shopify for real products in the target family, independent of other filters.
  const getModelCompatibility = (url, model) => {
    if (!model) return Promise.resolve(true);
    const key = compatibilityKey(url, model);
    if (compatibilityCache.has(key)) return Promise.resolve(compatibilityCache.get(key));
    const pending = (async () => {
      const probe = new URL(url.toString());
      Array.from(probe.searchParams.keys()).forEach(name => {
        if (name.startsWith("filter.") || name === "page" || name === "section_id" || name === "sections") probe.searchParams.delete(name);
      });
      probe.hash = "";
      probe.searchParams.set(getConfig().modelParamName, model);
      const sectionId = document.querySelector("case-model-filter")?.dataset.sectionId;
      const read = async (target) => {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 6000);
        try {
          const response = await fetch(target.toString(), { credentials: "same-origin", signal: controller.signal });
          if (!response.ok) return null;
          const doc = new DOMParser().parseFromString(await response.text(), "text/html");
          const root = doc.querySelector("case-model-filter[data-current-result-count]");
          const count = root?.getAttribute("data-current-result-count");
          return count !== null && count !== undefined && /^\d+$/.test(count) ? Number(count) > 0 : null;
        } finally { window.clearTimeout(timeout); }
      };
      try {
        if (sectionId) {
          const sectionUrl = new URL(probe.toString());
          sectionUrl.searchParams.set("section_id", sectionId);
          const result = await read(sectionUrl);
          if (result !== null) return result;
        }
        return await read(probe);
      } catch { return null; }
    })().then(result => {
      // Network failures are not evidence that a model is unsupported.
      if (result === null) compatibilityCache.delete(key);
      else compatibilityCache.set(key, result);
      return result;
    });
    compatibilityCache.set(key, pending);
    return pending;
  };

  const isModelCompatible = (url, model) => {
    if (!model) return true;
    const cached = compatibilityCache.get(compatibilityKey(url, model));
    return typeof cached === "boolean" ? cached : null;
  };

  const isClearModelLink = (link) => link.matches("[data-global-phone-clear-link], .empty-state facet-link a");

  const buildUrlWithModel = (url, model, config, options = {}) => {
    const nextUrl = new URL(url.toString());

    config.knownParams.forEach((paramName) => nextUrl.searchParams.delete(paramName));

    // Preserve the model unless Shopify has verified that this family has no matching products.
    const compatibleModel = isModelCompatible(nextUrl, model);
    if (model && compatibleModel !== false) {
      nextUrl.searchParams.set(config.modelParamName, model);
    }

    if (options.resetPage) {
      nextUrl.searchParams.delete("page");
    }

    return nextUrl;
  };

  const toSameOriginHref = (url) => {
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }

    return url.toString();
  };

  const updateLabels = (model) => {
    const config = getConfig();

    document.querySelectorAll(labelSelector).forEach((label) => {
      label.textContent = model || config.emptyLabel;
    });

    document.querySelectorAll(triggerSelector).forEach((trigger) => {
      trigger.setAttribute(
        "aria-label",
        model ? config.selectedLabel.replace("__MODEL__", model) : config.emptyTriggerLabel
      );
    });

    document.querySelectorAll(cardModelSelector).forEach((label) => {
      const incompatibleCollection = isModelCompatible(new URL(window.location.href), model) === false;
      label.textContent = incompatibleCollection ? "" : model;
      label.hidden = incompatibleCollection || !model;
    });

    document.querySelectorAll(clearSelector).forEach((button) => {
      button.hidden = !model;
    });
  };

  const showSelectionConfirmation = (model) => {
    const config = getConfig();
    let toast = document.querySelector("[data-global-phone-toast]");

    if (!toast) {
      toast = document.createElement("div");
      toast.className = "global-phone-selection-toast";
      toast.dataset.globalPhoneToast = "";
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      toast.setAttribute("aria-atomic", "true");
      document.body.appendChild(toast);
    }

    toast.textContent = model
      ? config.selectedConfirmation.replace("__MODEL__", model)
      : config.clearedConfirmation;
    toast.classList.add("is-visible");

    window.clearTimeout(toastTimeout);
    toastTimeout = window.setTimeout(() => {
      toast.classList.remove("is-visible");
    }, 2400);
  };

  const updateOptions = (model) => {
    document.querySelectorAll(optionSelector).forEach((option) => {
      const isSelected = normalize(option.dataset.model) === normalize(model);
      option.setAttribute("aria-selected", isSelected ? "true" : "false");
    });
  };

  const updateCallToAction = (model) => {
    const config = getConfig();

    document.querySelectorAll(ctaSelector).forEach((link) => {
      link.textContent = model
        ? config.viewCasesForLabel.replace("__MODEL__", model)
        : config.viewCasesLabel;
    });
  };

  const resetSearch = () => {
    document.querySelectorAll(searchSelector).forEach((input) => {
      input.value = "";
    });

    document.querySelectorAll("[data-global-phone-group]").forEach((group) => {
      group.hidden = false;

      group.querySelectorAll(optionSelector).forEach((option) => {
        option.hidden = false;
      });
    });

    document.querySelectorAll(emptySelector).forEach((empty) => {
      empty.hidden = true;
    });
  };

  const updateControlledButtons = () => {
    const drawer = getDrawer();
    const isOpen = drawer?.hasAttribute("open");

    document.querySelectorAll(triggerSelector).forEach((trigger) => {
      trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
    });
  };

  const applyModelToCollectionLinks = (model) => {
    const config = getConfig();

    document.querySelectorAll("a[href]").forEach((link) => {
      const originalHref = link.dataset.globalPhoneOriginalHref || link.getAttribute("href");

      if (!originalHref || originalHref.startsWith("#") || originalHref.startsWith("mailto:") || originalHref.startsWith("tel:")) {
        return;
      }

      let url;

      try {
        url = new URL(originalHref, window.location.origin);
      } catch (error) {
        return;
      }

      if (url.origin !== window.location.origin || !config.modelCollectionHandles.has(getCollectionHandle(url))) {
        return;
      }

      link.dataset.globalPhoneOriginalHref = originalHref;
      link.setAttribute("href", toSameOriginHref(buildUrlWithModel(url, isClearModelLink(link) ? "" : model, config, { resetPage: isClearModelLink(link) })));
      if (model && !isClearModelLink(link)) {
        getModelCompatibility(url, model).then(result => {
          if (!link.isConnected || normalize(selectedModel) !== normalize(model) || result === null) return;
          link.setAttribute("href", toSameOriginHref(buildUrlWithModel(url, model, config, { resetPage: result === false })));
        });
      }
    });
  };

  const getProductPickerForm = (picker) => {
    const formId = picker?.getAttribute("form-id");
    return formId ? document.forms[formId] : null;
  };

  const getProductModelInputs = (picker) => {
    const form = getProductPickerForm(picker);
    const position = picker?.dataset.phoneModelOptionPosition;

    if (!form || !position) return [];

    return Array.from(form.elements).filter((element) => (
      element.matches?.(`${productModelOptionSelector}[data-option-position="${position}"]`)
    ));
  };

  const formatProductModelMessage = (template, model) => (
    String(template || "").replace("__MODEL__", model || "")
  );

  const setProductPurchaseState = (picker, state, message = "", visibleModel = "") => {
    const form = getProductPickerForm(picker);
    if (!form) return;

    const isConfirmed = state === "confirmed";
    const placeholder = picker.dataset.phoneModelPlaceholder || "Choose phone model";
    const selectedValue = picker.querySelector("[data-product-model-value]");
    const messageElement = picker.querySelector("[data-product-model-message]");
    const idInput = form.querySelector('input[name="id"]');

    picker.dataset.phoneModelState = state;
    form.dataset.phoneModelConfirmed = isConfirmed ? "true" : "false";

    if (selectedValue) {
      selectedValue.textContent = isConfirmed ? visibleModel : placeholder;
    }

    if (messageElement) {
      messageElement.textContent = state === "missing" ? "" : message;
      messageElement.hidden = state === "missing" || !message;
    }

    if (idInput) {
      idInput.disabled = !isConfirmed;
    }

    form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((button) => {
      if (!button.dataset.phoneModelOriginalDisabled) {
        button.dataset.phoneModelOriginalDisabled = button.disabled ? "true" : "false";
      }

      if (!button.dataset.phoneModelOriginalContent && button.tagName === "BUTTON") {
        button.dataset.phoneModelOriginalContent = button.innerHTML;
      }

      button.disabled = !isConfirmed || button.dataset.phoneModelOriginalDisabled === "true";

      if (button.tagName === "BUTTON" && button.dataset.phoneModelOriginalContent) {
        button.innerHTML = isConfirmed ? button.dataset.phoneModelOriginalContent : placeholder;
      }
    });

    form.querySelectorAll(".shopify-payment-button").forEach((paymentButton) => {
      if (!paymentButton.dataset.phoneModelOriginalHidden) {
        paymentButton.dataset.phoneModelOriginalHidden = paymentButton.hidden ? "true" : "false";
      }

      paymentButton.hidden = !isConfirmed || paymentButton.dataset.phoneModelOriginalHidden === "true";
    });
  };

  const clearProductModelInputs = (picker) => {
    getProductModelInputs(picker).forEach((input) => {
      input.checked = false;
    });
  };

  const applyModelToProductPicker = (picker, model) => {
    if (!picker?.isConnected) return;

    const inputs = getProductModelInputs(picker);
    if (inputs.length === 0) return;

    if (!model) {
      clearProductModelInputs(picker);
      setProductPurchaseState(
        picker,
        "missing",
        picker.dataset.phoneModelRequiredMessage || ""
      );
      return;
    }

    const matchingInput = inputs.find((input) => normalize(input.dataset.optionLabel) === normalize(model));

    if (!matchingInput || matchingInput.disabled) {
      clearProductModelInputs(picker);
      setProductPurchaseState(
        picker,
        "unavailable",
        formatProductModelMessage(picker.dataset.phoneModelUnavailableMessage, model)
      );
      return;
    }

    if (matchingInput.checked) {
      setProductPurchaseState(picker, "confirmed", "", matchingInput.dataset.optionLabel || model);
      return;
    }

    setProductPurchaseState(picker, "pending", "", model);

    if (!window.customElements || !window.customElements.get("variant-picker")) {
      if (window.customElements) {
        window.customElements.whenDefined("variant-picker").then(() => {
          if (picker.isConnected) applyModelToProductPicker(picker, model);
        });
      }
      return;
    }

    matchingInput.click();
  };

  const applyModelToProductPickers = (model) => {
    document.querySelectorAll(productPickerSelector).forEach((picker) => {
      applyModelToProductPicker(picker, model);
    });
  };

  const findProductPickerForForm = (form) => (
    Array.from(document.querySelectorAll(productPickerSelector)).find((picker) => (
      picker.getAttribute("form-id") === form?.id
    ))
  );

  const bindProductModelRequirements = () => {
    if (productRequirementsBound) return;
    productRequirementsBound = true;

    document.addEventListener("change", (event) => {
      const input = event.target.closest?.(productModelOptionSelector);
      if (!input) return;

      const picker = Array.from(document.querySelectorAll(productPickerSelector)).find((candidate) => (
        candidate.getAttribute("form-id") === input.getAttribute("form")
      ));
      const model = input.dataset.optionLabel || "";

      if (!picker || !model) return;

      setSelectedModel(model, { persist: true });
      setProductPurchaseState(picker, "pending", "", model);
    });

    document.addEventListener("variant:change", (event) => {
      const form = event.target.closest?.("form") || event.target;
      const picker = findProductPickerForForm(form);

      if (picker) {
        window.requestAnimationFrame(() => applyModelToProductPicker(picker, getStoredModel()));
      }
    });

    document.addEventListener("roar:phone-model-change", (event) => {
      applyModelToProductPickers(event.detail?.model || "");
    });

    document.addEventListener("submit", (event) => {
      const picker = findProductPickerForForm(event.target);
      if (!picker || event.target.dataset.phoneModelConfirmed === "true") return;

      event.preventDefault();
      event.stopPropagation();

      const message = picker.querySelector("[data-product-model-message]");
      const selectButton = picker.querySelector("[data-product-model-option] .select");

      if (message) {
        message.hidden = false;
      }

      selectButton?.focus();
    }, true);
  };

  const setSelectedModel = (model, options = {}) => {
    const config = getConfig();
    selectedModel = model;

    if (options.persist !== false) {
      if (model) {
        storeModel(model);
      } else {
        clearStoredModel();
      }
    }

    resetSearch();
    updateLabels(model);
    updateOptions(model);
    updateCallToAction(model);
    applyModelToCollectionLinks(model);
    updateControlledButtons();

    document.dispatchEvent(new CustomEvent("roar:phone-model-change", {
      detail: { model }
    }));

    if (options.announce) {
      showSelectionConfirmation(model);
    }

    if (!options.navigateCollection) {
      return;
    }

    const currentUrl = new URL(window.location.href);

    if (!config.modelCollectionHandles.has(getCollectionHandle(currentUrl))) {
      config.drawer?.hide?.();
      return;
    }

    const nextUrl = buildUrlWithModel(currentUrl, model, config, { resetPage: true });

    if (nextUrl.toString() !== currentUrl.toString()) {
      if (options.announce) {
        try {
          window.sessionStorage.setItem(pendingConfirmationKey, model || "__ALL_MODELS__");
        } catch (error) {
          // The visible filter state still confirms the choice if session storage is unavailable.
        }
      }

      window.location.href = nextUrl.toString();
    } else {
      config.drawer?.hide?.();
    }
  };

  document.addEventListener("click", async (event) => {
    const link = event.target.closest?.("a[href]");
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute("download") || (link.target && link.target !== "_self")) return;
    const url = new URL(link.href, window.location.origin);
    const config = getConfig();
    if (url.origin !== window.location.origin || !config.modelCollectionHandles.has(getCollectionHandle(url))) return;
    if (isClearModelLink(link)) {
      familyNavigationRequest++;
      setSelectedModel("", { persist: true });
      try { window.sessionStorage.removeItem("rsCaseModelFilterRequestedModel"); } catch {}
      return;
    }
    const model = selectedModel;
    if (!model) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const request = ++familyNavigationRequest;
    link.setAttribute("aria-busy", "true");
    const compatible = await getModelCompatibility(url, model);
    link.removeAttribute("aria-busy");
    if (request !== familyNavigationRequest || normalize(selectedModel) !== normalize(model)) return;
    if (compatible === false) {
      setSelectedModel("", { persist: true });
      try { window.sessionStorage.removeItem("rsCaseModelFilterRequestedModel"); } catch {}
    }
    window.location.assign(buildUrlWithModel(url, compatible === false ? "" : model, config, { resetPage: true }).toString());
  }, true);

  window.addEventListener("pageshow", (event) => { if (event.persisted) init(); });

  document.addEventListener("roar:cart-cleared", () => setSelectedModel("", { persist: true }));

  const bindOptions = () => {
    document.querySelectorAll(optionSelector).forEach((option) => {
      if (option.dataset.globalPhoneBound === "true") {
        return;
      }

      option.dataset.globalPhoneBound = "true";
      option.addEventListener("click", () => {
        setSelectedModel(option.dataset.model, { navigateCollection: true, announce: true });
      });
    });
  };

  const bindClear = () => {
    document.querySelectorAll(clearSelector).forEach((button) => {
      if (button.dataset.globalPhoneBound === "true") {
        return;
      }

      button.dataset.globalPhoneBound = "true";
      button.addEventListener("click", () => {
        setSelectedModel("", { navigateCollection: true, announce: true });
      });
    });
  };

  const bindSearch = () => {
    document.querySelectorAll(searchSelector).forEach((input) => {
      if (input.dataset.globalPhoneBound === "true") {
        return;
      }

      input.dataset.globalPhoneBound = "true";
      input.addEventListener("input", () => {
        const query = normalize(input.value);
        let totalVisibleCount = 0;

        document.querySelectorAll("[data-global-phone-group]").forEach((group) => {
          let visibleCount = 0;

          group.querySelectorAll(optionSelector).forEach((option) => {
            const isVisible = matchesModelSearch(option.dataset.model, query);
            option.hidden = !isVisible;

            if (isVisible) {
              visibleCount += 1;
            }
          });

          group.hidden = visibleCount === 0;
          totalVisibleCount += visibleCount;
        });

        document.querySelectorAll(emptySelector).forEach((empty) => {
          empty.hidden = totalVisibleCount > 0;
        });
      });
    });
  };

  const init = () => {
    const config = getConfig();
    const currentUrl = new URL(window.location.href);
    const urlModel = getModelFromUrl(currentUrl, config.knownParams);
    const model = urlModel || getStoredModel();
    const shouldRestoreCollectionFilter = Boolean(
      model &&
      !urlModel &&
      isModelCompatible(currentUrl, model) !== false &&
      config.modelCollectionHandles.has(getCollectionHandle(currentUrl))
    );

    bindOptions();
    bindClear();
    bindSearch();
    bindProductModelRequirements();
    setSelectedModel(model, { persist: Boolean(urlModel) });
    if (shouldRestoreCollectionFilter) {
      getModelCompatibility(currentUrl, model).then(compatible => {
        if (normalize(selectedModel) !== normalize(model)) return;
        if (compatible === false) setSelectedModel("", { persist: true });
        else if (compatible === true) setSelectedModel(model, { navigateCollection: true });
      });
    }

    try {
      const pendingConfirmation = window.sessionStorage.getItem(pendingConfirmationKey);

      if (pendingConfirmation) {
        window.sessionStorage.removeItem(pendingConfirmationKey);
        showSelectionConfirmation(pendingConfirmation === "__ALL_MODELS__" ? "" : pendingConfirmation);
      }
    } catch (error) {
      // The selected model remains visible in the filter and product cards.
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
  document.addEventListener("dialog:after-show", updateControlledButtons);
  document.addEventListener("dialog:after-hide", updateControlledButtons);
  new MutationObserver((mutations) => {
    const hasNewProductCards = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => (
      node.nodeType === Node.ELEMENT_NODE &&
      (node.matches?.(cardModelSelector) || node.querySelector?.(cardModelSelector))
    )));
    const hasNewProductPicker = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => (
      node.nodeType === Node.ELEMENT_NODE &&
      (node.matches?.(productPickerSelector) || node.querySelector?.(productPickerSelector))
    )));

    if (hasNewProductCards) {
      updateLabels(selectedModel);
    }

    if (hasNewProductPicker) {
      window.requestAnimationFrame(() => applyModelToProductPickers(getStoredModel()));
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener("storage", (event) => {
    if (event.key === storageKey) {
      setSelectedModel(getStoredModel(), { persist: false });
    }
  });
})();

(() => {
  if(window.rsScreenProductBound)return;
  window.rsScreenProductBound=true;
  function update(){
    document.querySelectorAll('[data-rs-screen-product] form').forEach(form=>{
      if(form.dataset.phoneModelConfirmed!=='true')return;
      const picker=Array.from(document.querySelectorAll('variant-picker')).find(p=>p.getAttribute('form-id')===form.getAttribute('id'));
      let variant;try{variant=JSON.parse(picker?.querySelector('[data-variant]')?.textContent||'null');}catch{return;}
      const button=form.querySelector('button[type="submit"]');
      if(!variant?.available||!button||button.disabled||button.getAttribute('aria-busy')==='true')return;
      const quantityInput=Array.from(document.querySelectorAll('input[name="quantity"]')).find(input=>input.getAttribute('form')===form.getAttribute('id')||form.contains(input));
      const cents=variant.price*Number(quantityInput?.value||1);
      const price=new Intl.NumberFormat(document.documentElement.lang||'sv',{style:'currency',currency:window.Shopify?.currency?.active||'SEK',minimumFractionDigits:cents%100?2:0,maximumFractionDigits:2}).format(cents/100);
      const label=(document.documentElement.lang.startsWith('sv')?'Lägg i varukorgen':document.documentElement.lang.startsWith('da')?'Læg i kurv':'Add to cart')+' · '+price;
      if(button.textContent!==label)button.textContent=label;
    });
  }
  const schedule=()=>requestAnimationFrame(update);
  document.addEventListener('input',schedule);
  document.addEventListener('change',schedule);
  document.addEventListener('variant:change',schedule);
  document.addEventListener('roar:phone-model-change',schedule);
  document.addEventListener('click',e=>{if(e.target.closest('.quantity-selector'))schedule();});
  new MutationObserver(schedule).observe(document.documentElement,{subtree:true,attributes:true,attributeFilter:['data-phone-model-confirmed','data-phone-model-state']});
  update();
})();



(() => {
  const selector = '[data-product-model-popover]';
  const normalize = value => String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');

  // Match each search term independently while preserving the real variant inputs.
  const matchesModelSearch = (model, query) => {
    const plain = value => String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const compactModel = plain(model).replace(/[^a-z0-9]/g, '');
    const terms = plain(query).split(/[^a-z0-9]+/).filter(Boolean);
    return terms.every(term => compactModel.includes(term));
  };


  function filter(popover) {
    const input = popover.querySelector('[data-product-model-search]');
    const list = popover.querySelector('.popover__value-list');
    if (!input || !list) return;
    const query = input.value;
    let count = 0;
    list.querySelectorAll('[data-product-model-group]').forEach(group => {
      let visible = 0;
      group.querySelectorAll('.popover__value-option').forEach(option => {
        option.hidden = !matchesModelSearch(option.dataset.productModelSearchValue, query);
        if (!option.hidden) visible += 1;
      });
      group.hidden = visible === 0;
      count += visible;
    });
    const empty = popover.querySelector('[data-product-model-empty]');
    if (empty) empty.hidden = count > 0;
    list.scrollTop = 0;
  }

  function init() {
    document.querySelectorAll(selector).forEach(popover => {
      if (popover.dataset.productModelSearchReady) return;
      const list = popover.querySelector('.popover__value-list');
      if (!list) return;
      const options = Array.from(list.children).filter(option => option.matches('.popover__value-option'));
      if (!options.length) return;
      popover.dataset.productModelSearchReady = 'true';
      const groups = new Map();
      options.forEach(option => {
        const label = option.querySelector('[data-phone-model-option]')?.dataset.optionLabel || option.textContent.trim();
        const brand = /iphone/i.test(label) ? 'Apple' : /samsung|galaxy/i.test(label) ? 'Samsung' : '';
        option.dataset.productModelSearchValue = normalize(label);
        if (!groups.has(brand)) {
          const group = document.createElement('section');
          group.className = 'rs-product-model-group';
          group.dataset.productModelGroup = brand;
          if (brand) {
            const heading = document.createElement('h3');
            heading.className = 'rs-product-model-group__title';
            heading.id = popover.id + '-group-' + brand.toLowerCase();
            heading.textContent = brand;
            group.setAttribute('aria-labelledby', heading.id);
            group.appendChild(heading);
          }
          groups.set(brand, group);
        }
        groups.get(brand).appendChild(option);
      });
      ['Apple', 'Samsung', ''].forEach(brand => {
        if (groups.has(brand)) list.appendChild(groups.get(brand));
      });
      filter(popover);
    });
  }

  document.addEventListener('input', event => {
    if (event.target.matches('[data-product-model-search]')) filter(event.target.closest(selector));
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('[data-product-model-search]')) event.preventDefault();
  });
  document.addEventListener('dialog:after-show', event => {
    if (!event.target.matches?.(selector)) return;
    init();
    const input = event.target.querySelector('[data-product-model-search]');
    if (input) input.value = '';
    filter(event.target);
  });
  document.addEventListener('shopify:section:load', init);
  new MutationObserver(records => {
    if (records.some(record => Array.from(record.addedNodes).some(node =>
      node.nodeType === Node.ELEMENT_NODE && (node.matches?.(selector) || node.querySelector?.(selector))
    ))) init();
  }).observe(document.documentElement, {childList: true, subtree: true});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();


